import { getRemoteFeatureAccessMap } from "@/shared/services/remoteConfig.service";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { useAccountStore } from "@/account/stores/account";
import type { FeatureAccessConfig, FeatureAccessMap } from "@/shared/types/featureAccess";

const ACCOUNTS = [
  "acct_1R9BewFfSJTwIAFG",
  "acct_1Qz0gUFhuwgI8eek", // Nathan Local
  "acct_1RQxEBCI8pDNjXb2", // Jadin Local
  "acct_1RuwfyFewG45cSk2", // Jadin Local
  "acct_1RLpCrCNvN7yjhr1", // Jadin Develop
  "acct_1RLUU02EMFAZzKKB", // Stripe Lucas Demo
  "acct_1PxZJ1C15ZG8i1Xe", // Allie Demo
  "acct_1RnLsCIotWIpOyn5", // Allie Account
  "acct_1SsoTbFtLrjgGPpW", //YG
  "acct_1NT48nCEQf21la3t",
  "acct_1Sz2DpFuWJbghd4n", //Mike Account
];

/**
 * Feature access mapping - key: feature name, value: access configuration
 */
const baseFeatureAccessMap: FeatureAccessMap = {
  [PlatformFeature.INVENTORY]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
  [PlatformFeature.LOCATION_BASED_INVENTORY]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
  [PlatformFeature.TAXES]: {
    accounts: ACCOUNTS,
    enabledGlobally: true,
  },
  [PlatformFeature.ONLINE_PAYMENTS]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
  [PlatformFeature.KIOSK]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
  [PlatformFeature.RECEIPT_PRINTER]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
  [PlatformFeature.CUSTOM_EMAILED_RECEIPTS]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
  [PlatformFeature.WEBHOOKS]: {
    accounts: ACCOUNTS,
    enabledGlobally: false,
  },
};

let remoteFeatureAccessMap: FeatureAccessMap = {};
let remoteConfigPromise: Promise<void> | null = null;

const fetchRemoteFeatureAccess = async (): Promise<void> => {
  try {
    remoteFeatureAccessMap = await getRemoteFeatureAccessMap();
  } catch {
    remoteFeatureAccessMap = {};
  }
};

const ensureRemoteFeatureAccessInit = (): void => {
  if (!remoteConfigPromise) {
    remoteConfigPromise = fetchRemoteFeatureAccess();
  }
};

export const initFeatureAccess = async (): Promise<void> => {
  ensureRemoteFeatureAccessInit();
  try {
    await remoteConfigPromise;
  } catch {}
};

const getEffectiveFeatureConfig = (feature: string): FeatureAccessConfig | null => {
  const baseConfig = baseFeatureAccessMap[feature];
  const remoteConfig = remoteFeatureAccessMap[feature];

  if (!baseConfig && !remoteConfig) {
    return null;
  }

  const enabledGlobally = remoteConfig?.enabledGlobally ?? baseConfig?.enabledGlobally ?? false;

  const baseAccounts = baseConfig?.accounts ?? [];
  const remoteAccounts =
    remoteConfig && Array.isArray(remoteConfig.accounts) ? remoteConfig.accounts : [];
  const accounts = Array.from(new Set([...baseAccounts, ...remoteAccounts]));

  return {
    enabledGlobally,
    accounts,
  };
};

/**
 * Check if the current account has access to a specific feature
 * @param feature The feature name to check access for
 * @returns boolean indicating whether the current account has access to the feature
 */
export const hasFeatureAccess = (feature: string): boolean => {
  ensureRemoteFeatureAccessInit();

  const accountStore = useAccountStore();
  const currentAccountId = accountStore.getAccountId;

  const featureConfig = getEffectiveFeatureConfig(feature);

  if (!featureConfig) {
    return false;
  }

  // If the feature is enabled globally, grant access to all accounts
  if (featureConfig.enabledGlobally) {
    return true;
  }

  if (!currentAccountId) {
    return false;
  }

  // Otherwise, check if the current account ID is in the list of accounts with access
  return featureConfig.accounts.includes(currentAccountId);
};
